/**
 * MovingFormDialog — New / Edit for Sales Control › Moving (Internal Moving).
 * Converted 1:1 from Access `frmCtrlSalesQryMovingAdd` (Detail 2-column grid:
 * col1 Left=2412 / col2 Left=9588, rows by Top). Combos = Autocomplete
 * (store CODE, display "<CODE> — <NAME>"). truck_company=service_code,
 * package_code=packaging_code, pic=username stored codes; hydrate injects them
 * as current Autocomplete value. Required (Access btnSave errCheck order):
 * move_no ("Please type to generate Full Moving No!") + move_date
 * ("Please choose Moving Date!"); duplicate-on-New surfaced from BE 422.
 * move_no is auto-generated (readonly, "MOV"+padded index) via Suggest-No.
 */
import { useEffect, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type MovingLookupItem,
  type SaleMovingWrite,
  useLazySuggestMoveNoQuery,
  useLazyGetSaleMovingFormQuery,
  useMovingLookupWarehousesQuery,
  useMovingLookupQualitiesQuery,
  useMovingLookupCertificationsQuery,
  useMovingLookupTruckCompaniesQuery,
  useMovingLookupPackagesQuery,
  useMovingLookupPicsQuery,
  useCreateSaleMovingMutation,
  useUpdateSaleMovingMutation,
} from '../../store/api/saleControlApi'
import { FORM_GRID_2COL } from '../../theme/responsive'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

interface Props {
  open: boolean
  /** copy = hydrate from movId like edit, but save as a NEW record with a
   *  freshly suggested move_no (web-only addition; Access has no btnCopy). */
  mode: 'new' | 'edit' | 'copy'
  movId?: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  moveNo: string
  moveDate: string
  loadingDate: string
  netWeightKg: string
  noContTruck: string
  note: string
  fromWarehouse: string
  toWarehouse: string
  quality: string
  certification: string
  truckCompany: string
  packageCode: string
  pic: string
}

const EMPTY: Draft = {
  moveNo: '', moveDate: '', loadingDate: '', netWeightKg: '', noContTruck: '', note: '',
  fromWarehouse: '', toWarehouse: '', quality: '', certification: '', truckCompany: '',
  packageCode: '', pic: '',
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const s2 = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s2) return null
  const n = parseFloat(s2)
  return Number.isFinite(n) ? n : null
}
/** Access Format="Standard" — 2 decimals + thousands sep, right-aligned. */
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))

function withCurrent(options: MovingLookupItem[], value: string, label?: string): MovingLookupItem | null {
  if (!value) return null
  return options.find((o) => o.value === value) ?? { value, label: label || value }
}

/** Flat combobox: "<CODE> — <NAME>", collapse to code when code==label. */
function Combo(props: {
  label: string
  value: string
  valueLabel?: string
  options: MovingLookupItem[]
  loading?: boolean
  onInputChange?: (q: string) => void
  onPick: (value: string, item: MovingLookupItem | null) => void
}) {
  const { label, value, valueLabel, options, loading, onInputChange, onPick } = props
  const sel = withCurrent(options, value, valueLabel)
  const merged = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  // Dedupe by value — lookup có thể trả code trùng (vd warehouse 'LDC'); trùng
  // value → trùng Autocomplete option key → React duplicate-key warning.
  const seen = new Set<string>()
  const opts = merged.filter((o) => {
    const k = String(o.value)
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
  const getLabel = (o: MovingLookupItem) =>
    !o ? '' : o.value === o.label || !o.label ? String(o.value ?? '') : `${o.value} — ${o.label}`
  return (
    <Autocomplete
      size={SM}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => onPick(v?.value ?? '', v)}
      onInputChange={onInputChange ? (_e, v, reason) => { if (reason === 'input') onInputChange(v) } : undefined}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={onInputChange ? (x) => x : undefined}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  )
}

export default function MovingFormDialog({ open, mode, movId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [labels, setLabels] = useState<Record<string, string>>({})
  // Edit/Copy hydrate hits a view query → show a loading state until the API
  // response lands, then render the fields (avoid a flash of empty inputs).
  const [formLoading, setFormLoading] = useState(false)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  const [suggestMoveNo] = useLazySuggestMoveNoQuery()
  const [loadForm] = useLazyGetSaleMovingFormQuery()
  const [createMoving, { isLoading: creating }] = useCreateSaleMovingMutation()
  const [updateMoving, { isLoading: updating }] = useUpdateSaleMovingMutation()
  const saving = creating || updating

  // Lookups — load when dialog open.
  const skip = { skip: !open }
  const warehouses = useMovingLookupWarehousesQuery(undefined, skip)
  const qualities = useMovingLookupQualitiesQuery(undefined, skip)
  const certifications = useMovingLookupCertificationsQuery(undefined, skip)
  const [truckQ, setTruckQ] = useState('')
  const truckCompanies = useMovingLookupTruckCompaniesQuery(truckQ || undefined, skip)
  const packages = useMovingLookupPackagesQuery(undefined, skip)
  const [picQ, setPicQ] = useState('')
  const pics = useMovingLookupPicsQuery(picQ || undefined, skip)

  // Hydrate on open.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    if (mode !== 'new') setFormLoading(true) // show loading immediately for Edit/Copy
    void (async () => {
      if (mode === 'new') {
        setFormLoading(false)
        const resp = await suggestMoveNo().unwrap().catch(() => null)
        if (cancelled) return
        setLabels({})
        setDraft({ ...EMPTY, moveNo: resp?.moveNo ?? '', moveDate: dayjs().format('YYYY-MM-DD') })
        return
      }
      if (!movId) { setFormLoading(false); return }
      const f = await loadForm(movId).unwrap().catch(() => null)
      if (cancelled) return
      if (!f) { setFormLoading(false); return }
      // Copy: carry every field but regenerate move_no (unique business key).
      let moveNo = s((f as Record<string, unknown>).moveNo)
      if (mode === 'copy') {
        const resp = await suggestMoveNo().unwrap().catch(() => null)
        if (cancelled) return
        moveNo = resp?.moveNo ?? ''
      }
      const g = (k: string) => s((f as Record<string, unknown>)[k])
      setLabels({
        truckCompany: g('truckName'), packageCode: g('packaging'), pic: g('fullName'),
      })
      setDraft({
        ...EMPTY,
        moveNo,
        moveDate: g('moveDate').slice(0, 10),
        loadingDate: g('loadingDate').slice(0, 10),
        netWeightKg: fmtStd(g('netWeightKg')),
        noContTruck: g('noContTruck'),
        note: g('note'),
        fromWarehouse: g('fromWarehouse'),
        toWarehouse: g('toWarehouse'),
        quality: g('quality'),
        certification: g('certification'),
        truckCompany: g('truckCompany'),
        packageCode: g('packageCode'),
        pic: g('pic'),
      })
      setFormLoading(false)
    })()
    return () => { cancelled = true }
  }, [open, mode, movId, suggestMoveNo, loadForm])

  const handleClose = () => { if (!saving) onClose() }

  const canSave = canEdit && !saving

  const handleSave = async () => {
    if (!canEdit || saving) return
    // Access btnSave errCheck order: move_no empty → 3; move_date empty → 4.
    if (!draft.moveNo.trim()) { snack.error('Please type to generate Full Moving No!'); return }
    if (!draft.moveDate.trim()) { snack.error('Please choose Moving Date!'); return }

    const payload: SaleMovingWrite = {
      moveNo: draft.moveNo || null,
      moveDate: draft.moveDate || null,
      loadingDate: draft.loadingDate || null,
      netWeightKg: parseNum(draft.netWeightKg),
      fromWarehouse: draft.fromWarehouse || null,
      toWarehouse: draft.toWarehouse || null,
      quality: draft.quality || null,
      certification: draft.certification || null,
      packageCode: draft.packageCode || null,
      noContTruck: draft.noContTruck || null,
      truckCompany: draft.truckCompany || null,
      pic: draft.pic || null,
      note: draft.note || null,
    }
    try {
      if (mode === 'edit' && movId) {
        await updateMoving({ id: movId, body: payload }).unwrap()
        snack.success(`Moving ${draft.moveNo} updated`)
      } else {
        await createMoving(payload).unwrap()
        snack.success(`Moving ${draft.moveNo} added`)
      }
      onSaved()
      onClose()
    } catch (err) {
      // BE 422 "This Full Moving No. is existed!" surfaced here.
      snack.error(getErrorMessage(err, 'Save moving failed'))
    }
  }

  const title = mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'

  const dateField = (key: keyof Draft, label: string) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{ textField: { size: SM, fullWidth: true, InputLabelProps: { shrink: true } } }}
    />
  )

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>{title} — Moving</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {formLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
        <>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit movings.</Alert>}
        {/* Access frmCtrlSalesQryMovingAdd Detail — 2-col grid (col1 Left=2412 /
            col2 Left=9588), rows by Top. moving_order_scs_id + moving_no_index
            (auto index) omitted; move_no auto-generated readonly. */}
        <Box sx={FORM_GRID_2COL}>
          {/* Row Top=96: move_no (col2) */}
          <TextField label="Full Moving No." size={SM} fullWidth value={draft.moveNo}
            InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
          {dateField('moveDate', 'Moving Date')}

          {/* Row Top=1164: from_warehouse / to_warehouse */}
          <Combo label="From Warehouse" value={draft.fromWarehouse} options={warehouses.data?.items ?? []}
            loading={warehouses.isFetching} onPick={(v) => set('fromWarehouse', v)} />
          <Combo label="To Warehouse" value={draft.toWarehouse} options={warehouses.data?.items ?? []}
            loading={warehouses.isFetching} onPick={(v) => set('toWarehouse', v)} />

          {/* Row Top=1704: loading_date / net_weight_kg */}
          {dateField('loadingDate', 'Loading Date')}
          <TextField label="NW kgs" size={SM} fullWidth type="text" value={draft.netWeightKg}
            onChange={(e) => set('netWeightKg', e.target.value)}
            onBlur={() => set('netWeightKg', fmtStd(draft.netWeightKg))}
            inputProps={numProps} />

          {/* Row Top=2232: quality / certification */}
          <Combo label="Quality" value={draft.quality} options={qualities.data?.items ?? []}
            loading={qualities.isFetching} onPick={(v) => set('quality', v)} />
          <Combo label="Certification" value={draft.certification} options={certifications.data?.items ?? []}
            loading={certifications.isFetching} onPick={(v) => set('certification', v)} />

          {/* Row Top=2760: package_code / pic */}
          <Combo label="Package" value={draft.packageCode} options={packages.data?.items ?? []}
            loading={packages.isFetching} onPick={(v) => set('packageCode', v)} />
          <Combo label="PIC" value={draft.pic} valueLabel={labels.pic} options={pics.data?.items ?? []}
            loading={pics.isFetching} onInputChange={setPicQ} onPick={(v) => set('pic', v)} />

          {/* Row Top=3300: no_cont_truck / truck_company */}
          <TextField label="No. cont/truck" size={SM} fullWidth value={draft.noContTruck}
            onChange={(e) => set('noContTruck', e.target.value)} />
          <Combo label="Truck Co." value={draft.truckCompany} valueLabel={labels.truckCompany}
            options={truckCompanies.data?.items ?? []} loading={truckCompanies.isFetching}
            onInputChange={setTruckQ} onPick={(v) => set('truckCompany', v)} />

          {/* Row Top=3828: note (full width) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <TextField label="Note" size={SM} fullWidth multiline minRows={2} value={draft.note}
              onChange={(e) => set('note', e.target.value)} />
          </Box>
        </Box>
        </>
        )}
      </DialogContent>
    </Dialog>
  )
}
